import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  TextInput,
  Checkbox,
  NumberInput,
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Select
} from '@etherisc/ui-kit';
import { PlusIcon, TrashIcon } from 'lucide-react';
import type { CoverageConfig, StrikeExhaust } from '../../types/product';
import type { AdministrativeUnit } from '../../types/administrativeUnit';
import { 
  getPerilOptions,
  activities,
  getActivityById
} from '../../config/products';
import { CoverageLevelGlobalLimitsCard } from './CoverageLevelGlobalLimitsCard';
import { getAdministrativeLevelLabel } from '../../utils/administrativeLevelLabels';

/**
 * Normalize activity identifier (name or ID) to activity ID
 * Handles backward compatibility with products that might have names stored
 */
function normalizeActivityToId(activityIdentifier: string): string | null {
  // First check if it's already a valid ID
  if (getActivityById(activityIdentifier)) {
    return activityIdentifier;
  }
  
  // Try to find by name (case-insensitive)
  const foundById = activities.find(
    a => a.name.toLowerCase() === activityIdentifier.toLowerCase()
  );
  if (foundById) {
    return foundById.id;
  }
  
  // Try to find by aliases (case-insensitive)
  const foundByAlias = activities.find(
    a => a.aliases?.some(alias => alias.toLowerCase() === activityIdentifier.toLowerCase())
  );
  if (foundByAlias) {
    return foundByAlias.id;
  }
  
  // Not found - return null
  return null;
}

interface CoverageConfigFormProps {
  coverageConfig: CoverageConfig;
  onChange: (config: CoverageConfig) => void;
  errors?: Array<{
    path: string;
    message: string;
    value: unknown;
  }>;
  readOnly?: boolean;
  availableAdminUnits?: AdministrativeUnit[];
}

export function CoverageConfigForm({
  coverageConfig,
  onChange,
  errors = [], // TODO: Implement error handling in form fields
  readOnly = false,
  availableAdminUnits = []
}: CoverageConfigFormProps) {
  // Suppress unused variable warning temporarily
  void errors;
  const [newActivity, setNewActivity] = useState('');
  const [areaSearchQuery, setAreaSearchQuery] = useState('');
  const [areaDropdownOpen, setAreaDropdownOpen] = useState(false);
  const areaDropdownRef = useRef<HTMLDivElement>(null);
  
  // Get configuration options
  const perilOptions = getPerilOptions();
  
  // Normalize activitiesOrProducts to ensure only IDs are stored
  // This handles backward compatibility with products that might have names stored
  const normalizeActivities = (activitiesList: string[]): string[] => {
    return activitiesList
      .map(activityIdentifier => normalizeActivityToId(activityIdentifier))
      .filter((id): id is string => id !== null); // Remove any that couldn't be normalized
  };
  
  // Track if we've normalized to prevent infinite loops
  const normalizedRef = useRef<string>('');
  
  // Normalize on mount and when coverageConfig.activitiesOrProducts changes
  useEffect(() => {
    if (readOnly) return; // Don't normalize in read-only mode
    
    const currentActivities = coverageConfig.activitiesOrProducts || [];
    const currentKey = currentActivities.join(',');
    
    // Skip if we've already normalized this exact list
    if (normalizedRef.current === currentKey) return;
    
    const normalizedActivities = normalizeActivities(currentActivities);
    const normalizedKey = normalizedActivities.join(',');
    
    // Only update if normalization changed something
    if (normalizedKey !== currentKey) {
      normalizedRef.current = normalizedKey;
      onChange({
        ...coverageConfig,
        activitiesOrProducts: normalizedActivities
      });
    } else {
      normalizedRef.current = currentKey;
    }
  }, [coverageConfig.activitiesOrProducts?.join(',')]); // Only when activities list changes
  
  // Get available activities (filter out already selected ones)
  // Use normalized activities for display
  const selectedActivityIds = normalizeActivities(coverageConfig.activitiesOrProducts || []);
  const availableActivities = activities
    .filter(activity => !selectedActivityIds.includes(activity.id))
    .map(activity => ({
      value: activity.id,
      label: activity.name,
      description: activity.description
    }));

  const areaOptions = useMemo(() => {
    const selectedAreas = new Set(coverageConfig.areaOptions || []);
    return availableAdminUnits
      .filter(unit => !selectedAreas.has(unit.adminName))
      .map(unit => ({
        id: unit.id,
        name: unit.adminName,
        level: unit.adminLevel,
        label: `${unit.adminName} (${getAdministrativeLevelLabel(unit.adminLevel)})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableAdminUnits, coverageConfig.areaOptions]);

  const filteredAreaOptions = useMemo(() => {
    if (!areaSearchQuery.trim()) return areaOptions;
    const q = areaSearchQuery.toLowerCase();
    return areaOptions.filter(opt => opt.label.toLowerCase().includes(q));
  }, [areaOptions, areaSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(e.target as Node)) {
        setAreaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFieldChange = (field: keyof CoverageConfig, value: unknown) => {
    if (readOnly) return;
    onChange({
      ...coverageConfig,
      [field]: value
    });
  };

  const handlePerilChange = (perilId: string, checked: boolean) => {
    if (readOnly) return;
    const newPerils = checked 
      ? [...(coverageConfig.perils || []), perilId]
      : (coverageConfig.perils || []).filter(p => p !== perilId);
    handleFieldChange('perils', newPerils);
  };

  const handleAddActivity = () => {
    if (!newActivity || readOnly) return;
    // Validate that the activity ID exists in the activities config
    const activity = getActivityById(newActivity);
    if (!activity) return;
    
    // Prevent duplicate entries
    if (selectedActivityIds.includes(newActivity)) return;
    
    const newActivities = [...selectedActivityIds, newActivity];
    handleFieldChange('activitiesOrProducts', newActivities);
    setNewActivity('');
  };

  const handleRemoveActivity = (index: number) => {
    if (readOnly) return;
    const newActivities = (coverageConfig.activitiesOrProducts || []).filter((_, i) => i !== index);
    handleFieldChange('activitiesOrProducts', newActivities);
  };

  const handleAddAreaOption = (name: string) => {
    if (!name.trim() || readOnly) return;
    const current = coverageConfig.areaOptions || [];
    if (current.includes(name)) return;
    handleFieldChange('areaOptions', [...current, name]);
    setAreaSearchQuery('');
    setAreaDropdownOpen(false);
  };

  const handleRemoveAreaOption = (index: number) => {
    if (readOnly) return;
    const newAreaOptions = (coverageConfig.areaOptions || []).filter((_, i) => i !== index);
    handleFieldChange('areaOptions', newAreaOptions);
  };

  const handleCoveragePeriodTypeChange = (type: 'date_range' | 'duration_days') => {
    if (readOnly) return;
    if (type === 'date_range') {
      handleFieldChange('coveragePeriodLimits', {
        type: 'date_range',
        startDateRange: { earliest: '', latest: '' },
        endDateRange: { earliest: '', latest: '' }
      });
    } else {
      handleFieldChange('coveragePeriodLimits', {
        type: 'duration_days',
        minDurationDays: 1,
        maxDurationDays: 365
      });
    }
  };

  const handleCoveragePeriodFieldChange = (field: string, value: unknown) => {
    if (readOnly) return;
    handleFieldChange('coveragePeriodLimits', {
      ...(coverageConfig.coveragePeriodLimits || {}),
      [field]: value
    });
  };

  const handleDateRangeChange = (rangeType: 'startDateRange' | 'endDateRange', field: 'earliest' | 'latest', value: string) => {
    if (readOnly) return;
    const currentLimits = coverageConfig.coveragePeriodLimits || {};
    if (currentLimits.type === 'date_range') {
      handleFieldChange('coveragePeriodLimits', {
        ...currentLimits,
        [rangeType]: {
          ...(currentLimits[rangeType] || {}),
          [field]: value
        }
      });
    }
  };

  const sanitizeNumericValue = (value: number): number => {
    return Number(String(value).replace(/^0+(?=\d)/, ''));
  };

  const handleSumInsuredChange = (field: 'min' | 'max', value: number) => {
    if (readOnly) return;
    handleFieldChange('sumInsuredLimits', {
      ...(coverageConfig.sumInsuredLimits || {}),
      [field]: sanitizeNumericValue(value)
    });
  };



  const handleCoverageLevelGlobalLimitsChange = (limits: Record<string, StrikeExhaust>) => {
    if (readOnly) return;
    handleFieldChange('coverageLevelGlobalLimits', limits);
  };

  return (
    <div className="space-y-6">
      {/* Activities/Products */}
      <Card>
        <CardHeader>
          <CardTitle>Activities & Products</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Eligible Activities or Products</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedActivityIds.map((activityId, index) => {
                const activity = getActivityById(activityId);
                return (
                <Badge key={index} variant="secondary" className="flex items-center gap-2">
                    {activity?.name || activityId}
                  {!readOnly && (
                    <button
                      onClick={() => handleRemoveActivity(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon size={14} />
                    </button>
                  )}
                </Badge>
                );
              })}
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                  value={newActivity}
                    onValueChange={setNewActivity}
                    placeholder={availableActivities.length > 0 ? "Select activity or product" : "All activities selected"}
                    options={availableActivities}
                    disabled={availableActivities.length === 0}
                  />
                </div>
                <Button 
                  onClick={handleAddActivity} 
                  variant="outline"
                  disabled={!newActivity || selectedActivityIds.includes(newActivity) || availableActivities.length === 0}
                >
                  <PlusIcon size={16} className="mr-2" />
                  Add
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Perils */}
      <Card>
        <CardHeader>
          <CardTitle>Covered Perils</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {perilOptions.map((peril) => (
              <div key={peril.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`peril-${peril.value}`}
                  checked={(coverageConfig.perils || []).includes(peril.value)}
                  onCheckedChange={(checked) => handlePerilChange(peril.value, !!checked)}
                  disabled={readOnly}
                />
                <label htmlFor={`peril-${peril.value}`} className="text-sm font-medium">
                  {peril.label}
                  {peril.common && <span className="ml-1 text-xs text-blue-600">(common)</span>}
                </label>
                {peril.description && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-gray-500 ml-2 cursor-help">ℹ️</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{peril.description}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Area Options */}
      <Card>
        <CardHeader>
          <CardTitle>Geographic Areas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Available Area Options</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {(coverageConfig.areaOptions || []).map((area, index) => (
                <Badge key={index} variant="outline" className="flex items-center gap-2">
                  {area}
                  {!readOnly && (
                    <button
                      onClick={() => handleRemoveAreaOption(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon size={14} />
                    </button>
                  )}
                </Badge>
              ))}
              {(coverageConfig.areaOptions || []).length === 0 && (
                <span className="text-gray-500 text-sm">No areas selected</span>
              )}
            </div>
            {!readOnly && (
              <div className="relative" ref={areaDropdownRef}>
                <TextInput
                  value={areaSearchQuery}
                  onChange={(e) => {
                    setAreaSearchQuery(e.target.value);
                    setAreaDropdownOpen(true);
                  }}
                  onFocus={() => setAreaDropdownOpen(true)}
                  placeholder="Search administrative units..."
                  className="w-full"
                />
                {areaDropdownOpen && filteredAreaOptions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filteredAreaOptions.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        onClick={() => handleAddAreaOption(opt.name)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                {areaDropdownOpen && areaSearchQuery.trim() && filteredAreaOptions.length === 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg p-3 text-sm text-gray-500">
                    No matching administrative units
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coverage Period Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Coverage Period Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {/* Period Type and Date Validation side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Period Type</label>
                <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="duration-days"
                    name="periodType"
                    checked={coverageConfig.coveragePeriodLimits?.type === "duration_days"}
                    onChange={() => handleCoveragePeriodTypeChange("duration_days")}
                    disabled={readOnly}
                  />
                  <label htmlFor="duration-days" className="text-sm">Duration in Days</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="date-range"
                    name="periodType"
                    checked={coverageConfig.coveragePeriodLimits?.type === "date_range"}
                    onChange={() => handleCoveragePeriodTypeChange("date_range")}
                    disabled={readOnly}
                  />
                  <label htmlFor="date-range" className="text-sm">Date Range</label>
                </div>
                </div>
              </div>

              {/* Date Validation */}
              <div>
                <label className="text-sm font-medium">Date Validation</label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="date-validation-none"
                      name="dateValidation"
                      checked={coverageConfig.coveragePeriodLimits?.dateValidation === "none"}
                      onChange={() => handleCoveragePeriodFieldChange('dateValidation', 'none')}
                      disabled={readOnly}
                    />
                    <label htmlFor="date-validation-none" className="text-sm">None (any date allowed)</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="date-validation-pentads"
                      name="dateValidation"
                      checked={coverageConfig.coveragePeriodLimits?.dateValidation === "pentads" || coverageConfig.coveragePeriodLimits?.dateValidation === undefined}
                      onChange={() => handleCoveragePeriodFieldChange('dateValidation', 'pentads')}
                      disabled={readOnly}
                    />
                    <label htmlFor="date-validation-pentads" className="text-sm">Pentads (start: 1,6,11,16,21... end: 5,10,15,20,25...)</label>
                  </div>
                </div>
              </div>
            </div>

            {coverageConfig.coveragePeriodLimits?.type === "duration_days" && (
              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  label="Minimum Duration (Days)"
                  value={coverageConfig.coveragePeriodLimits?.minDurationDays || 1}
                  onChange={(e) => handleCoveragePeriodFieldChange('minDurationDays', Number(e.target.value))}
                  min={1}
                  disabled={readOnly}
                />
                <NumberInput
                  label="Maximum Duration (Days)"
                  value={coverageConfig.coveragePeriodLimits?.maxDurationDays || 365}
                  onChange={(e) => handleCoveragePeriodFieldChange('maxDurationDays', Number(e.target.value))}
                  min={1}
                  disabled={readOnly}
                />
              </div>
            )}

            {coverageConfig.coveragePeriodLimits?.type === "date_range" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Start Date Range</label>
                  <div className="grid grid-cols-2 gap-4">
                    <TextInput
                      label="Earliest Start Date"
                      type="date"
                      value={coverageConfig.coveragePeriodLimits?.startDateRange?.earliest || ""}
                      onChange={(e) => handleDateRangeChange('startDateRange', 'earliest', e.target.value)}
                      disabled={readOnly}
                    />
                    <TextInput
                      label="Latest Start Date"
                      type="date"
                      value={coverageConfig.coveragePeriodLimits?.startDateRange?.latest || ""}
                      onChange={(e) => handleDateRangeChange('startDateRange', 'latest', e.target.value)}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">End Date Range</label>
                  <div className="grid grid-cols-2 gap-4">
                    <TextInput
                      label="Earliest End Date"
                      type="date"
                      value={coverageConfig.coveragePeriodLimits?.endDateRange?.earliest || ""}
                      onChange={(e) => handleDateRangeChange('endDateRange', 'earliest', e.target.value)}
                      disabled={readOnly}
                    />
                    <TextInput
                      label="Latest End Date"
                      type="date"
                      value={coverageConfig.coveragePeriodLimits?.endDateRange?.latest || ""}
                      onChange={(e) => handleDateRangeChange('endDateRange', 'latest', e.target.value)}
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cutoff Date */}
            <div>
              <TextInput
                label="Cutoff Date (Last day allowed for payments)"
                type="date"
                value={coverageConfig.cutoffDate || ""}
                onChange={(e) => {
                  const newDate = e.target.value;
                  onChange({
                    ...coverageConfig,
                    cutoffDate: newDate || undefined
                  });
                }}
                disabled={readOnly}
                min={new Date().toISOString().split('T')[0]}
                title="Last date where payment for this product is allowed (must be in the future)"
              />
              {coverageConfig.cutoffDate && new Date(coverageConfig.cutoffDate) <= new Date() && (
                <p className="mt-1 text-sm text-red-600">Cutoff date must be in the future</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sum Insured Limits - Full Width */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Sum Insured Limits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <NumberInput
              label="Minimum Sum Insured"
              value={coverageConfig.sumInsuredLimits?.min || 0}
              onChange={(e) => handleSumInsuredChange('min', Number(e.target.value))}
              onBlur={(e) => {
                const sanitized = sanitizeNumericValue(Number(e.target.value));
                if (sanitized !== Number(e.target.value)) {
                  handleSumInsuredChange('min', sanitized);
                }
              }}
              min={0}
              disabled={readOnly}
            />
            <NumberInput
              label="Maximum Sum Insured"
              value={coverageConfig.sumInsuredLimits?.max || 1000000}
              onChange={(e) => handleSumInsuredChange('max', Number(e.target.value))}
              onBlur={(e) => {
                const sanitized = sanitizeNumericValue(Number(e.target.value));
                if (sanitized !== Number(e.target.value)) {
                  handleSumInsuredChange('max', sanitized);
                }
              }}
              min={0}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

        {/* Show new v1.5.0 Coverage Level Global Limits or legacy v1.4.0 Coverage Level Limits */}
        <CoverageLevelGlobalLimitsCard
          limits={coverageConfig.coverageLevelGlobalLimits || {}}
          onChange={handleCoverageLevelGlobalLimitsChange}
          availablePerils={coverageConfig.perils || []}
          readOnly={readOnly}
        />


    </div>
  );
}
